import { ChangeEvent } from 'react';
import { useTranslation } from 'react-i18next';
import AssetDetailSection from '../../assets/components/AssetDetailSection';
import DetailField from '../../assets/components/DetailField';
import InputField from '../../../components/ui/InputField';
import SelectField from '../../../components/ui/SelectField';
import Toggle from '../../../components/ui/Toggle';
import { formatAddressLine } from '../../../utils/addressLine';
import type { StationDetailResponse } from '../../../api/station/stationApiTypes';
import CoordinatesFields from '../../../components/location/CoordinatesFields';
import ImageSlot from '../../../components/ImageSlot';
import type { LocationDraft } from '../stationLocationDraft';

/** The upload gate of the server's location-photo route (inventoryRoutes). */
export const LOCATION_PHOTO_MIME = new Set(['image/png', 'image/jpeg', 'image/webp', 'image/svg+xml']);
export const MAX_LOCATION_PHOTO_BYTES = 5 * 1024 * 1024;

interface Props {
    station: StationDetailResponse;
    mode: 'view' | 'edit';
    draft: LocationDraft;
    onDraftChange: (next: LocationDraft) => void;
    /** Object URL of a picked photo, for the edit preview. */
    pickedPhotoUrl: string | null;
    /** A picked file the page refused (type, size). */
    photoError: string | null;
    onPhotoPicked: (file: File) => void;
}

/**
 * The IANA zones this browser knows, plus the stored one (#3219). The stored
 * name is added when missing because the server keeps the spelling its own ICU
 * resolves to (`Europe/Kiev` on an older ICU, `Europe/Kyiv` on a newer one),
 * and a select that cannot show the current value reads as "no zone set".
 */
const timeZoneOptions = (current: string): string[] => {
    const supported = (Intl as { supportedValuesOf?: (key: 'timeZone') => string[] })
        .supportedValuesOf?.('timeZone') ?? [];
    // An empty value is never an option: Radix throws on `<Select.Item value="">`
    // (see `SelectField`), and a read without `timeZone` must not crash the form.
    return !current || supported.includes(current) ? supported : [current, ...supported];
};


/** The address: read, or its four fields where it is editable. */
const LocationAddress = ({ editing, addressFixed, stored, draft, onChange }: {
    editing: boolean;
    addressFixed: boolean;
    stored: NonNullable<StationDetailResponse['location']>['address'];
    draft: LocationDraft;
    onChange: (patch: Partial<LocationDraft>) => void;
}) => {
    const { t } = useTranslation();
    if (!editing || addressFixed) {
        return (
            <>
                <DetailField label={t('station_detail.location.address')} value={formatAddressLine(stored) || null} />
                {editing && (
                    <p className={'text-sm text-foreground/60'}>{t('station_detail.location.immovable')}</p>
                )}
            </>
        );
    }
    return (
        <>
            <div className={'grid grid-cols-1 sm:grid-cols-[2fr_1fr] gap-4'}>
                <InputField
                    label={t('station_detail.location.street')}
                    value={draft.street}
                    onChange={(street) => onChange({ street })}
                />
                <InputField
                    label={t('station_detail.location.house_number')}
                    value={draft.houseNumber}
                    onChange={(houseNumber) => onChange({ houseNumber })}
                />
            </div>
            <div className={'grid grid-cols-1 sm:grid-cols-[1fr_2fr] gap-4'}>
                <InputField
                    label={t('station_detail.location.postal_code')}
                    value={draft.postalCode}
                    onChange={(postalCode) => onChange({ postalCode })}
                />
                <InputField
                    label={t('station_detail.location.city')}
                    value={draft.city}
                    onChange={(city) => onChange({ city })}
                />
            </div>
        </>
    );
};

/** The photo: shown square, or the square slot while editing. */
const LocationPhoto = ({ editing, storedSrc, shownSrc, error, onSelect, onRemove }: {
    editing: boolean;
    storedSrc: string | null;
    shownSrc: string | null;
    error: string | null;
    onSelect: (e: ChangeEvent<HTMLInputElement>) => void;
    onRemove: () => void;
}) => {
    const { t } = useTranslation();
    return (
        <div className={'max-w-[240px]'}>
            {editing ? (
                <ImageSlot
                    label={t('station_detail.location.photo_label')}
                    help={t('station_detail.location.photo_help')}
                    previewSrc={shownSrc}
                    onSelect={onSelect}
                    onRemove={onRemove}
                    chooseLabel={t('station_detail.location.photo_choose')}
                    removeLabel={t('station_detail.location.photo_remove')}
                    noImageLabel={t('station_detail.location.photo_none')}
                    aspectClass={'aspect-square'}
                    targetRatio={1}
                    ratioWarning={t('station_detail.location.photo_ratio_warning')}
                />
            ) : (
                <>
                    <p className={'text-sm text-foreground/60 mb-1'}>{t('station_detail.location.photo_label')}</p>
                    <div
                        className={'w-full aspect-square rounded-md border border-foreground/15'
                            + ' bg-muted overflow-hidden flex items-center justify-center'}
                    >
                        {storedSrc ? (
                            <img src={storedSrc} alt={t('station_detail.location.photo_label')} className={'w-full h-full object-cover'} />
                        ) : (
                            <span className={'text-sm text-foreground/40'}>{t('station_detail.location.photo_none')}</span>
                        )}
                    </div>
                </>
            )}
            {error && <p className={'mt-1 text-sm text-error'} role={'alert'}>{error}</p>}
        </div>
    );
};

type LocationStatusValue = { enabled: boolean, bookable: boolean };

/** Aktiv / Buchbar — shared by everything at the address (plan room-houses §6). */
const LocationStatus = ({ editing, stored, draft, onChange }: {
    editing: boolean;
    stored: LocationStatusValue;
    draft: LocationStatusValue | null;
    onChange: (status: LocationStatusValue) => void;
}) => {
    const { t } = useTranslation();
    const shown = editing && draft ? draft : stored;
    return (
        <div className={'flex flex-col gap-3'}>
            <Toggle
                label={t('common.active')}
                checked={shown.enabled}
                disabled={!editing}
                onChange={(enabled) => onChange({ ...shown, enabled })}
            />
            <Toggle
                label={t('common.bookable')}
                description={t('station_detail.location.status_shared')}
                checked={shown.bookable}
                disabled={!editing}
                onChange={(bookable) => onChange({ ...shown, bookable })}
            />
        </div>
    );
};

/**
 * The address and what belongs to it — the LOCATION's, not the station's:
 * district, map pin, photo, status, time zone. The opening hours are the
 * location's too and have their own section (`StationOpeningHoursSection`).
 *
 * ── One "Bearbeiten" for the page (PO, 2026-10-19) ─────────────────────────
 *
 * The section has no buttons of its own. The page's "Bearbeiten" opens every
 * section at once, and its one "Speichern" writes them — the rule of
 * `docs/design-decisions.md` › Bearbeiten und Speichern, as on the room page.
 * Photo and the Aktiv/Buchbar toggles used to save on click; they are part of
 * the edit now and are written on save. The draft lives on the page.
 *
 * ── Shared with every station at the address ───────────────────────────────
 *
 * A location can hold several stations (an office with a meeting room and a
 * locker). In edit mode the section names them: a change here reaches every
 * one of them, and an operator who does not realise that is how one room
 * becomes two locations (PO, 2026-09-10). The address is edited IN PLACE —
 * nothing here moves the station to another location or forks a new one.
 *
 * ── What stays editable where the address is fixed ─────────────────────────
 *
 * A room-station IS its location and a house stands where it was built, so
 * their street/number/postcode/city are fixed (`locationEditable` comes from
 * the server). District, map pin and time zone are not where the building
 * stands; they stay editable, sent apart from `address` so the server's
 * immovable-address refusal never applies to them.
 */
const StationLocationSection = ({
    station, mode, draft, onDraftChange, pickedPhotoUrl, photoError, onPhotoPicked,
}: Props) => {
    const { t } = useTranslation();
    const location = station.location;
    const addressFixed = !station.locationEditable;
    const editing = mode === 'edit';
    const set = (patch: Partial<LocationDraft>) => onDraftChange({ ...draft, ...patch });

    if (!location) {
        return (
            <AssetDetailSection title={t('station_detail.sections.location')}>
                {/* Not an empty card: a station written before #2445 has no
                    location row, and "nothing here" would read as "no address
                    entered" rather than "this station predates the fix". */}
                <p className={'text-sm text-foreground/60'}>
                    {t('station_detail.location.missing')}
                </p>
            </AssetDetailSection>
        );
    }

    const siblings = location.otherStations;
    // A pin or district needs an address row to sit on; a location written
    // before #2445 has none until its address is entered here.
    const hasAddressRow = !!location.address || !addressFixed;
    const shownPhoto = draft.removePhoto ? null : (pickedPhotoUrl ?? location.images?.[0] ?? null);

    const handlePhotoSelect = (e: ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0] ?? null;
        e.target.value = '';
        if (file) onPhotoPicked(file);
    };

    return (
        <AssetDetailSection title={t('station_detail.sections.location')}>
            <div className={'flex flex-col gap-4 max-w-xl'}>
                {/* The sibling stations, named: a change reaches every one. */}
                {siblings.length > 0 && (
                    <div className={'rounded-[5px] bg-foreground/5 px-4 py-3'}>
                        <p className={'text-sm text-foreground'}>
                            {editing
                                ? t('station_detail.location.dialog_shared', {
                                    stations: [station.name, ...siblings.map((s) => s.name)].join(', '),
                                })
                                : t('station_detail.location.shared_with', { count: siblings.length })}
                        </p>
                        {!editing && (
                            <ul className={'mt-1 list-disc pl-5 text-sm text-foreground/70'}>
                                {siblings.map((sibling) => (
                                    <li key={sibling.id}>{sibling.name}</li>
                                ))}
                            </ul>
                        )}
                    </div>
                )}

                <LocationAddress
                    editing={editing}
                    addressFixed={addressFixed}
                    stored={location.address}
                    draft={draft}
                    onChange={set}
                />

                {editing && hasAddressRow ? (
                    <InputField
                        label={t('asset_detail.fields.district')}
                        help={t('station_detail.location.district_help')}
                        value={draft.district}
                        onChange={(district) => set({ district })}
                    />
                ) : (
                    <DetailField
                        label={t('asset_detail.fields.district')}
                        value={location.address?.district || null}
                    />
                )}

                {editing && hasAddressRow ? (
                    <CoordinatesFields
                        value={draft.pin}
                        onChange={(pin) => set({ pin })}
                        addressAbove={!addressFixed}
                        addressParts={addressFixed
                            ? [location.address?.street, location.address?.houseNumber,
                                location.address?.postalCode, location.address?.city]
                            : [draft.street, draft.houseNumber, draft.postalCode, draft.city]}
                    />
                ) : (
                    <DetailField
                        label={t('coordinates.label')}
                        value={location.address?.geoLat != null && location.address?.geoLng != null
                            ? `${location.address.geoLat}, ${location.address.geoLng}`
                            : t('coordinates.not_set')}
                    />
                )}

                <LocationPhoto
                    editing={editing}
                    storedSrc={location.images?.[0] ?? null}
                    shownSrc={shownPhoto}
                    error={photoError}
                    onSelect={handlePhotoSelect}
                    onRemove={() => set({ photoFile: null, removePhoto: true })}
                />

                {/* Shared by everything at the address, so it lives with the
                    location and says so (plan room-houses §6: accepted). */}
                {location.status && (
                    <LocationStatus
                        editing={editing}
                        stored={location.status}
                        draft={draft.status}
                        onChange={(status) => set({ status })}
                    />
                )}

                {editing ? (
                    <SelectField
                        label={t('station_detail.location.time_zone')}
                        help={t('station_detail.location.time_zone_help')}
                        value={draft.timeZone}
                        onChange={(timeZone) => set({ timeZone })}
                        options={timeZoneOptions(draft.timeZone)}
                        labelAbove
                    />
                ) : (
                    <DetailField
                        label={t('station_detail.location.time_zone')}
                        value={location.timeZone || null}
                    />
                )}
            </div>
        </AssetDetailSection>
    );
};

export default StationLocationSection;
